import { useEffect, useState } from 'react'
import { fetchAssortment, type Assortment } from './landing'
import { TENANT } from './tenant'
import { SEGMENTS, segmentApiValue } from './segments'
import { cachedAssortment, rememberAssortment } from './assortmentCache'

type Result = { scope: string; data?: Assortment; error?: boolean; fresh: boolean }

export function useAssortment() {
  const scope = TENANT.manufacturer
  const [attempt, setAttempt] = useState(0)
  // Start from this scope's last known assortment (stale-while-revalidate); `settled` turns
  // true only when the fresh request for this mount has answered.
  const [result, setResult] = useState<Result | null>(() => {
    const data = cachedAssortment<Assortment>(scope)
    return data ? { scope, data, fresh: false } : null
  })
  useEffect(() => {
    let cancelled = false
    setResult(prev => {
      const data = prev?.scope === scope ? prev.data : cachedAssortment<Assortment>(scope)
      return data ? { scope, data, fresh: false } : null
    })
    fetchAssortment().then(data => {
      if (cancelled) return
      rememberAssortment(scope, data)
      setResult({ scope, data, fresh: true })
    }).catch(() => { if (!cancelled) setResult({ scope, error: true, fresh: true }) })
    return () => { cancelled = true }
  }, [scope, attempt])
  const current = result?.scope === scope ? result : null
  const data = current?.data
  return { data, error: !!current?.error, settled: !!current?.fresh,
    retry: () => setAttempt(n => n + 1),
    segments: data ? SEGMENTS.filter(s => s === 'ALL' || data.segments.includes(segmentApiValue(s)!)) : [] }
}
